/**
 * Platform-agnostic API client for WovenDays
 *
 * Web: createApiClient({ baseUrl: '', credentials: 'include' })
 * Mobile: createApiClient({ baseUrl: API_URL, getHeaders: () => bearerHeaders })
 */

export interface ApiClientConfig {
  baseUrl: string;
  getHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
  credentials?: RequestCredentials;
}

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public data: unknown
  ) {
    const msg = typeof data === 'object' && data !== null && 'error' in data
      ? String((data as Record<string, unknown>).error)
      : `HTTP ${status}`;
    super(msg);
    this.name = 'ApiClientError';
  }
}

export interface ApiClient {
  get<T = unknown>(path: string): Promise<T>;
  post<T = unknown>(path: string, body?: unknown): Promise<T>;
  put<T = unknown>(path: string, body?: unknown): Promise<T>;
  patch<T = unknown>(path: string, body?: unknown): Promise<T>;
  delete<T = unknown>(path: string): Promise<T>;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const extraHeaders = config.getHeaders ? await config.getHeaders() : {};

    const response = await fetch(`${config.baseUrl}${path}`, {
      ...options,
      ...(config.credentials ? { credentials: config.credentials } : {}),
      headers: {
        'Content-Type': 'application/json',
        ...extraHeaders,
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiClientError(response.status, data);
    }

    return data as T;
  }

  return {
    get<T = unknown>(path: string): Promise<T> {
      return request<T>(path, { method: 'GET' });
    },
    post<T = unknown>(path: string, body?: unknown): Promise<T> {
      return request<T>(path, {
        method: 'POST',
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    },
    put<T = unknown>(path: string, body?: unknown): Promise<T> {
      return request<T>(path, {
        method: 'PUT',
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    },
    patch<T = unknown>(path: string, body?: unknown): Promise<T> {
      return request<T>(path, {
        method: 'PATCH',
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    },
    delete<T = unknown>(path: string): Promise<T> {
      return request<T>(path, { method: 'DELETE' });
    },
  };
}
